export type ComponentType = 'ram' | 'storage';
export interface HardwareAction {
  component_type: ComponentType;
  direction: 'installed' | 'recovered';
  accessory_id: string;
  quantity: number;
  unit_cost: number;
}

/** A specific part name wins over a broad/mixed supplier category. */
export function detectComponentType(part: { name: string; category?: string | null }): ComponentType | null {
  const name = part.name.toLowerCase();
  const ram = /\b(?:ram|memory|ddr[2-5]?|sodimm|so-dimm)\b/.test(name);
  const storage = /\b(?:ssd|hdd|nvme|storage|hard\s*(?:disk|drive))\b/.test(name);
  if (ram !== storage) return ram ? 'ram' : 'storage';
  if (ram && storage) return null;
  const category = (part.category ?? '').trim().toLowerCase();
  if (['ram', 'memory'].includes(category)) return 'ram';
  if (['storage', 'ssd', 'hdd', 'nvme', 'hard drive'].includes(category)) return 'storage';
  return null;
}

/** Missing/unknown is not proof that a physical component was absent. */
export function explicitlyAbsent(value: string | null | undefined, type: ComponentType): boolean {
  const text = (value ?? '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  if (['none', 'nil'].includes(text)) return true;
  return type === 'ram'
    ? /^(?:no ram(?: installed)?|no memory(?: installed)?|without ram)$/.test(text)
    : /^(?:no (?:ssd|hdd|storage|drive|disk)(?: installed)?|without (?:ssd|hdd|storage|drive)|diskless)$/.test(text);
}

function compareSpec(value: string | null | undefined, type: ComponentType): string {
  if (explicitlyAbsent(value, type)) return '';
  return (value ?? '').toLowerCase().replace(/\s+/g, '').trim();
}

export function resolveSaleHardware(input: {
  label: string;
  originalRam?: string | null;
  originalStorage?: string | null;
  finalRam?: string;
  finalStorage?: string;
  clearRam?: boolean;
  clearStorage?: boolean;
  actions: HardwareAction[];
}): { ram: string | null; storage: string | null; clear_ram: boolean; clear_storage: boolean } {
  const seen = new Set<string>();
  for (const action of input.actions) {
    if (!['ram', 'storage'].includes(action.component_type) || !['installed', 'recovered'].includes(action.direction)) {
      throw new Error('Choose RAM or Storage and Install or Recover for every component action.');
    }
    if (!Number.isSafeInteger(action.quantity) || action.quantity < 1) throw new Error('Component quantity must be a positive whole number.');
    if (!Number.isFinite(action.unit_cost) || action.unit_cost < 0) throw new Error('Component cost must be zero or a positive number.');
    const key = `${action.component_type}:${action.direction}:${action.accessory_id}`;
    if (seen.has(key)) throw new Error('This component action is already listed. Edit its quantity instead of adding it twice.');
    seen.add(key);
  }
  const resolve = (type: ComponentType, original: string | null | undefined, final: string | undefined, clear?: boolean) => {
    const label = type === 'ram' ? 'RAM' : 'Storage';
    const actions = input.actions.filter(a => a.component_type === type);
    const oldValue = original?.trim() || null;
    const requested = clear ? '' : final === undefined ? oldValue : final.trim();
    const oldKey = compareSpec(oldValue, type);
    const newKey = compareSpec(requested, type);
    if (explicitlyAbsent(oldValue, type) && actions.some(a => a.direction === 'recovered')) {
      throw new Error(`${input.label}: recorded ${label} is already absent. Remove the Recover action; only return a part that was physically in this laptop. If the inventory record is wrong, correct it first.`);
    }
    if (oldKey !== newKey) {
      if (actions.length === 0) throw new Error(`${input.label}: ${label} changed. Add a ${label} component action; an action for the other component does not cover this change.`);
      if (!newKey && !actions.some(a => a.direction === 'recovered')) throw new Error(`${input.label}: removing ${label} requires a Recover action for the part returned to stock.`);
      if (newKey && !oldKey && !actions.some(a => a.direction === 'installed')) throw new Error(`${input.label}: adding ${label} requires an Install action.`);
    }
    // Preserve equivalent stored text. This avoids inventing a hardware change
    // for '8 Gb' -> '8GB' or 'No HDD' -> 'NONE' under the existing database contract.
    if (oldKey === newKey) return { value: oldValue, clear: false };
    return { value: newKey ? requested || null : null, clear: !newKey };
  };
  const ram = resolve('ram', input.originalRam, input.finalRam, input.clearRam);
  const storage = resolve('storage', input.originalStorage, input.finalStorage, input.clearStorage);
  return { ram: ram.value, storage: storage.value, clear_ram: ram.clear, clear_storage: storage.clear };
}
